import React, { Suspense, useCallback, useLayoutEffect, useMemo, useState } from "react";
import type { Localizer } from "@/main/react/Types";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import {
    buildPassageExcerpt,
    type PassageAnchoredThoughtSummary,
    type PassageBlockMap,
} from "@/main/react/discussion/passage/mapBlocksToDom";

const PassageThread = lazyWithRetry(
    () => import("@/main/react/discussion/passage/PassageThread"),
    "passage-thread",
);

export type PassageMarkersProps = {
    localizer: Localizer;
    discussionSlug?: string;
    /** `mapBlocksToDom`'s answer for this body, or null when the join was refused: then nothing renders. */
    blockMap: PassageBlockMap | null;
    anchoredThoughts: ReadonlyArray<PassageAnchoredThoughtSummary>;
    /** The element the markers are positioned against; the body's `<article>`, which is `relative`. */
    positionRoot: HTMLElement | null;
    /**
     * Opens the composer anchored to this block, with the block's leading text as the excerpt. Absent on
     * a read-only post, which hides the CTA inside the sheet while the thread stays readable.
     */
    onCommentOnBlock?: (blockId: string, excerpt: string) => void;
};

type MarkerRow = {
    blockId: string;
    element: HTMLElement;
    thoughts: PassageAnchoredThoughtSummary[];
};

/**
 * Discovery half of passage anchors (design §4.2, A5, A7): one marker per block that already has at
 * least one comment on it, showing the bare count, positioned in the article's right gutter at the
 * block's top edge. Added AFTER mount, beside the body rather than inside it: the body node is
 * server-rendered HTML that `useBodyMediaLightbox` and the read trackers also hold, and rule 32 says JS
 * may only add to it. At zero anchors this component returns null, so an eligible post with no comments
 * on any passage renders the exact DOM it renders today.
 *
 * <p>Markers are real buttons in reading order (§9), so they are keyboard-reachable; the count is the
 * visible label and `passage.marker.aria` is the accessible name (A7: never "{0} comments"). REMOVED
 * anchors have no block to mark and are left to their chips in the tree.
 */
export default function PassageMarkers({
    localizer,
    discussionSlug,
    blockMap,
    anchoredThoughts,
    positionRoot,
    onCommentOnBlock,
}: PassageMarkersProps) {
    const rows = useMemo<MarkerRow[]>(() => {
        if (!blockMap || blockMap.size === 0 || anchoredThoughts.length === 0) {
            return [];
        }
        const byBlock = new Map<string, PassageAnchoredThoughtSummary[]>();
        for (const thought of anchoredThoughts) {
            if (thought.state === "REMOVED") {
                continue;
            }
            const list = byBlock.get(thought.blockId);
            if (list) {
                if (!list.some((existing) => existing.publicId === thought.publicId)) {
                    list.push(thought);
                }
            } else {
                byBlock.set(thought.blockId, [thought]);
            }
        }
        const result: MarkerRow[] = [];
        // Iterate the MAP so markers come out in document order, whatever order the thoughts arrived in.
        for (const [blockId, element] of blockMap) {
            const thoughts = byBlock.get(blockId);
            if (thoughts && thoughts.length > 0) {
                result.push({ blockId, element, thoughts });
            }
        }
        return result;
    }, [anchoredThoughts, blockMap]);

    // Vertical offsets, measured after layout and re-measured when the body resizes (images decoding,
    // the condensed clamp opening). Keyed by block id so a re-measure that changes nothing keeps the
    // same object and re-renders nothing.
    const [tops, setTops] = useState<Record<string, number>>({});
    useLayoutEffect(() => {
        if (rows.length === 0 || !positionRoot) {
            return;
        }
        const measure = () => {
            const rootTop = positionRoot.getBoundingClientRect().top;
            const next: Record<string, number> = {};
            for (const row of rows) {
                next[row.blockId] = Math.max(0, Math.round(row.element.getBoundingClientRect().top - rootTop));
            }
            setTops((previous) => {
                const previousKeys = Object.keys(previous);
                const nextKeys = Object.keys(next);
                const unchanged = previousKeys.length === nextKeys.length
                    && nextKeys.every((key) => previous[key] === next[key]);
                return unchanged ? previous : next;
            });
        };
        measure();
        if (typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(measure);
        observer.observe(positionRoot);
        return () => observer.disconnect();
    }, [positionRoot, rows]);

    const [openBlockId, setOpenBlockId] = useState<string | null>(null);
    const openRow = openBlockId ? rows.find((row) => row.blockId === openBlockId) ?? null : null;
    const openPassageText = useMemo(
        () => (openRow ? buildPassageExcerpt(openRow.element.textContent) : ""),
        [openRow],
    );

    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setOpenBlockId(null);
        }
    }, []);

    const handleCommentOnPassage = useCallback(() => {
        if (!openRow || !onCommentOnBlock) {
            return;
        }
        const blockId = openRow.blockId;
        const excerpt = openPassageText;
        setOpenBlockId(null);
        if (excerpt) {
            onCommentOnBlock(blockId, excerpt);
        }
    }, [onCommentOnBlock, openPassageText, openRow]);

    if (rows.length === 0) {
        return null;
    }

    const markerAriaLabel = localizer("passage.marker.aria");

    return (
        <>
            <div className="passage-marker-column" data-playwright-id="passage-marker-column">
                {rows.map((row) => (
                    <button
                        key={row.blockId}
                        type="button"
                        // `touch-hit-44` (FI-064): the chip stays 24 px, its coarse-pointer hit area becomes
                        // 44×44 through the utility's `::after`. `absolute` restates what `.passage-marker` already
                        // sets in CSS so the tap-target contract can see the containing block on this line.
                        className="passage-marker absolute touch-hit-44"
                        style={{ top: `${tops[row.blockId] ?? 0}px` }}
                        data-playwright-id="passage-marker"
                        data-block-id={row.blockId}
                        data-count={row.thoughts.length}
                        aria-label={markerAriaLabel}
                        aria-haspopup="dialog"
                        aria-expanded={openBlockId === row.blockId}
                        onPointerEnter={() => PassageThread.preload()}
                        onFocus={() => PassageThread.preload()}
                        onClick={() => setOpenBlockId(row.blockId)}
                    >
                        {row.thoughts.length}
                    </button>
                ))}
            </div>
            {openRow ? (
                /* Containment (FI-070, 2026-09-15): the sheet is opened by a press on a marker that is
                   already on screen, so a chunk that never arrives costs the sheet, not the discussion
                   island; the markers stay, and the boundary logs and marks the failure. */
                <LazyControlBoundary controlName="passage-thread" fallback={null}>
                <Suspense fallback={null}>
                    <PassageThread
                        localizer={localizer}
                        open={true}
                        onOpenChange={handleOpenChange}
                        discussionSlug={discussionSlug}
                        passageText={openPassageText}
                        thoughts={openRow.thoughts}
                        onCommentOnPassage={onCommentOnBlock ? handleCommentOnPassage : undefined}
                    />
                </Suspense>
                </LazyControlBoundary>
            ) : null}
        </>
    );
}
